import { getApiKey } from "./addin-auth";
import type {
    RedlineFailure,
    RedlineFailureReport,
    RedlineFailureStage,
    SnapshotStatus,
} from "./redline-failure";
import { readAttemptedPayload } from "./redline-failure";
import { uploadRedlineFailureSnapshot } from "./redline-failure-api";
import { buildRedlineFailureAttempt } from "./redline-failure-attempt";
import { captureRedlineFailureEvent } from "./redline-failure-sentry";
import type { RedlineApplyContext, RedlineSnapshotCapture } from "./redline-snapshot";
import { mintSnapshotId } from "./redline-snapshot";

type ReportedStages = Map<RedlineFailureStage, RedlineFailureReport>;

function contentAddressOfTheDocument(
    capture: RedlineSnapshotCapture,
    capturedAt: string,
): string {
    if (capture.status === "captured") return capture.documentBase64;
    if (capture.status === "too_large") return capture.documentDigest;
    return capturedAt;
}

function documentStatusOf(capture: RedlineSnapshotCapture["status"]): SnapshotStatus {
    if (capture === "capture_failed") return "capture_failed";
    if (capture === "not_captured") return "not_captured";
    return "stored";
}

const REPORTED_STAGES = new WeakMap<RedlineApplyContext, ReportedStages>();

function readReportedStages(applyContext: RedlineApplyContext): ReportedStages {
    const existing = REPORTED_STAGES.get(applyContext);
    if (existing) {
        return existing;
    }

    const created: ReportedStages = new Map();
    REPORTED_STAGES.set(applyContext, created);
    return created;
}

export interface ReportRedlineFailureArgs {
    applyContext: RedlineApplyContext;
    failure: RedlineFailure;
    error: Error;
}

export async function reportRedlineFailure({
    applyContext,
    failure,
    error,
}: ReportRedlineFailureArgs): Promise<RedlineFailureReport> {
    async function resolveSnapshot(): Promise<RedlineFailureReport> {
        const { capture } = applyContext;
        const documentBase64 = capture.status === "captured" ? capture.documentBase64 : null;

        const attempt = buildRedlineFailureAttempt({
            failure,
            error,
            reviewId: applyContext.reviewId,
        });

        let snapshotId: string | null = null;
        try {
            snapshotId = await mintSnapshotId({
                documentBase64: contentAddressOfTheDocument(capture, attempt.capturedAt),
                attemptedPayload: readAttemptedPayload(failure),
                stage: failure.stage,
            });

            if (capture.status === "too_large") {
                return { snapshotId, snapshotStatus: "too_large" };
            }

            const apiKey = getApiKey();
            if (!apiKey) {
                return { snapshotId, snapshotStatus: "upload_failed" };
            }

            const uploaded = await uploadRedlineFailureSnapshot({
                authHeader: `Bearer ${apiKey}`,
                snapshotId,
                documentBase64,
                attempt,
            });

            if (uploaded === "too_large") {
                return { snapshotId, snapshotStatus: "too_large" };
            }
            return { snapshotId, snapshotStatus: documentStatusOf(capture.status) };
        } catch {
            return { snapshotId, snapshotStatus: "upload_failed" };
        }
    }

    const reportedStages = readReportedStages(applyContext);
    const latched = reportedStages.get(failure.stage);
    if (latched) {
        return latched;
    }

    const report = await resolveSnapshot();
    captureRedlineFailureEvent({ applyContext, failure, error, report });

    reportedStages.set(failure.stage, report);
    return report;
}
